import { useEffect, useMemo, useState } from 'react';
import './CollaborationInvite.css';

function Reveal({ children, style }) {
  return (
    <div
      data-reveal
      style={{
        opacity: 0,
        transform: 'translateY(28px)',
        transition: 'opacity 0.9s ease, transform 0.9s ease',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Divider() {
  return (
    <div
      style={{
        height: 1,
        margin: '0 32px',
        background: 'linear-gradient(90deg, transparent, oklch(80% 0.03 45), transparent)',
      }}
    />
  );
}

export default function CollaborationInvite() {
  const [lightbox, setLightbox] = useState({ src: null, label: '' });

  const galleryA = useMemo(
    () =>
      Array.from({ length: 28 }, (_, i) => {
        const n = 28 - i;
        const label = `作品 ${String(n).padStart(2, '0')}`;
        return { id: `a-gallery-${n}`, label, src: `/images/portfolio/${n}.webp` };
      }),
    []
  );

  const customImg1 = '/images/costumes/1.webp';
  const customImg2 = '/images/costumes/2.webp';
  const swimImg1 = '/images/swimsuits/blue.webp';
  const swimImg2 = '/images/swimsuits/black.webp';
  const swimImg3 = '/images/swimsuits/grey.webp';
  const swimImg4 = '/images/swimsuits/red.webp';

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll('[data-reveal]').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const openLightbox = (e) => setLightbox({ src: e.currentTarget.src, label: e.currentTarget.alt });
  const closeLightbox = () => setLightbox({ src: null, label: '' });
  const stopClick = (e) => e.stopPropagation();

  const bodyText = { fontSize: 15, lineHeight: 2, margin: 0, color: 'oklch(34% 0.02 40)' };
  const eyebrow = { fontSize: 11, letterSpacing: '0.2em', color: 'oklch(60% 0.05 40)', margin: '0 0 10px' };
  const heading = { fontFamily: "'Noto Serif TC',serif", fontSize: 22, fontWeight: 600, margin: '0 0 20px' };
  const strong = { color: 'oklch(45% 0.07 35)' };
  const link = { color: 'oklch(48% 0.08 35)', textDecoration: 'underline' };
  const listStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    paddingLeft: 18,
    margin: 0,
    fontSize: 14.5,
    lineHeight: 1.9,
    color: 'oklch(34% 0.02 40)',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        fontFamily: "'Noto Sans TC',sans-serif",
        background: 'oklch(90% 0.015 50)',
      }}
    >
      <div
        style={{
          width: 430,
          maxWidth: '100vw',
          background: 'oklch(97% 0.014 55)',
          color: 'oklch(26% 0.02 40)',
          overflow: 'hidden',
        }}
      >
        {/* HERO */}
        <div
          style={{
            position: 'relative',
            padding: '100px 32px 72px',
            textAlign: 'center',
            overflow: 'hidden',
            background: 'linear-gradient(180deg, oklch(95% 0.02 45), oklch(97% 0.014 55))',
          }}
        >
          <div
            className="mizugi-invite-float-1"
            style={{
              position: 'absolute',
              top: 36,
              right: 26,
              width: 60,
              height: 60,
              borderRadius: '50%',
              border: '1px solid oklch(75% 0.05 40 / 0.5)',
            }}
          />
          <div
            className="mizugi-invite-float-2"
            style={{
              position: 'absolute',
              bottom: 24,
              left: 18,
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: 'oklch(85% 0.05 45 / 0.5)',
            }}
          />
          <p style={{ fontSize: 12, letterSpacing: '0.28em', color: 'oklch(55% 0.05 40)', margin: '0 0 18px' }}>
            MIZUGI SPACE
          </p>
          <h1
            style={{
              fontFamily: "'Noto Serif TC',serif",
              fontSize: 34,
              fontWeight: 600,
              lineHeight: 1.5,
              margin: '0 0 14px',
              letterSpacing: '0.02em',
            }}
          >
            競泳寫真
            <br />
            合作邀約
          </h1>
          <p style={{ fontSize: 14, color: 'oklch(48% 0.02 40)', margin: 0, letterSpacing: '0.05em' }}>
            日系競賽泳裝 攝影企劃
          </p>
          <div style={{ width: 36, height: 1, background: 'oklch(60% 0.04 40)', margin: '26px auto 0' }} />
        </div>

        {/* 企劃簡介 */}
        <Reveal style={{ padding: '56px 32px 48px' }}>
          <p style={eyebrow}>01</p>
          <h2 style={heading}>企劃簡介</h2>
          <p style={{ ...bodyText, margin: '0 0 16px' }}>
            Mizugi Space 是一個專門拍攝「競泳水著」的攝影企劃，主要以貼身、高衩的日系競賽泳裝為主。
          </p>
          <p style={{ ...bodyText, margin: '0 0 16px' }}>
            是一個完全<strong style={strong}>「傳教」</strong>向的攝影企劃。
          </p>
          <p style={bodyText}>
            為了讓大家知道競賽泳裝的美好之處，透過拍攝寫真、影片以及自製泳裝，甚至請日本繪師協助設計
            <a href="/aose-hiyori" style={{ ...link, textDecorationColor: 'oklch(75% 0.05 40)' }}>
              官方原創角色：青瀬ひより
            </a>
            來讓更多人能認識這個好看的服裝，完全是一個因為熱愛所以開始的攝影企劃。
          </p>
        </Reveal>

        <Divider />

        {/* 合作方式 */}
        <Reveal style={{ padding: '48px 32px' }}>
          <p style={eyebrow}>02</p>
          <h2 style={heading}>合作方式</h2>
          <p style={{ ...bodyText, margin: '0 0 16px' }}>
            採用<strong style={strong}>商用買斷制</strong>，希望能由您來報價。
          </p>
          <p style={{ ...bodyText, margin: '0 0 16px' }}>其他支出：場地、服裝、車馬費等所有支出皆由我方負擔。</p>
          <p style={bodyText}>如果您有其他合作方式或想法，也非常歡迎一起討論！</p>
        </Reveal>

        <Divider />

        {/* 拍攝地點 */}
        <Reveal style={{ padding: '48px 32px' }}>
          <p style={eyebrow}>03</p>
          <h2 style={heading}>拍攝地點</h2>
          <p style={bodyText}>
            拍攝地點通常在出租的攝影棚中，全台灣都可以配合前往您方便的區域附近的攝影棚，拍攝期間會以包場的形式租賃，因此不用擔心會與其他人共用場地。
          </p>
        </Reveal>

        <Divider />

        {/* 是否可以攜伴 */}
        <Reveal style={{ padding: '48px 32px' }}>
          <p style={eyebrow}>04</p>
          <h2 style={heading}>是否可以攜伴</h2>
          <p style={{ ...bodyText, margin: '0 0 16px' }}>當然可以！非常歡迎您帶朋友或小幫手一起來。</p>
          <p style={bodyText}>若需要協助調整動作或服裝，都可以交給小幫手處理，拍攝過程中我將不會與您有任何肢體接觸。</p>
        </Reveal>

        <Divider />

        {/* 作品發佈準備以及使用範圍 */}
        <Reveal style={{ padding: '48px 32px' }}>
          <p style={eyebrow}>05</p>
          <h2 style={heading}>作品發佈準備以及使用範圍</h2>
          <ul style={listStyle}>
            <li>拍攝完成後，我們會先把成品上傳雲端給您，您可以指定刪掉不喜歡的照片，或提出修改意見。</li>
            <li>
              最終作品會發佈在<strong style={strong}>Mizugi Space 官網上販售</strong>（
              <a href="https://mizugi.space" style={link}>
                mizugi.space
              </a>
              ），以及我們的{' '}
              <a href="https://www.instagram.com/mizugi.space/" style={link}>
                Instagram
              </a>
              、
              <a href="https://x.com/mizugispace" style={link}>
                X
              </a>
              、
              <a href="https://www.facebook.com/space.mizugi" style={link}>
                Facebook
              </a>
              。
            </li>
            <li>部分作品可能會製作成實體商品，並在官網上販售。</li>
            <li>
              您也可以在<strong style={strong}>非商業用途</strong>下自由使用照片（例如分享在
              IG），如果能順手標記我的社群帳號，我們會非常感謝您！
            </li>
          </ul>
        </Reveal>

        <Divider />

        {/* 拍攝內容 */}
        <Reveal style={{ padding: '48px 32px' }}>
          <p style={eyebrow}>06</p>
          <h2 style={heading}>拍攝內容</h2>
          <p style={{ ...bodyText, margin: '0 0 24px' }}>
            每次拍攝時間 2 小時，會拍攝 1～2 套衣服，最終發布時會全部一起發成一部作品。
          </p>

          <p style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.05em', color: 'oklch(45% 0.07 35)', margin: '0 0 8px' }}>
            第一套 · 日系 JK 制服 × 泳裝
          </p>
          <ul style={{ ...listStyle, gap: 10, margin: '0 0 28px' }}>
            <li>JK 水手服＋競泳水著</li>
            <li>會拍攝「制服狀態」、「半脫露出泳裝」以及「全脫只穿泳裝」等不同情境，具體可參考下方作品集。</li>
          </ul>

          <p style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.05em', color: 'oklch(45% 0.07 35)', margin: '0 0 8px' }}>
            第二套 · 特別主題 × 泳裝（可能會有）
          </p>
          <ul style={{ ...listStyle, gap: 10, margin: '0 0 20px' }}>
            <li>例如：女僕裝＋泳裝、秘書裝＋泳裝……等多種變化。</li>
            <li>因為這件特別主題的服裝會是我自己縫製的，有可能想不到比較好的題材，或是我的技術還不夠還做不出來，但是總之會在拍攝前與您討論確認主題。</li>
          </ul>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <img
              src={customImg1}
              alt="自製服裝範例 01"
              onClick={openLightbox}
              style={{ width: '100%', aspectRatio: '3 / 4', objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in', display: 'block' }}
            />
            <img
              src={customImg2}
              alt="自製服裝範例 02"
              onClick={openLightbox}
              style={{ width: '100%', aspectRatio: '3 / 4', objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in', display: 'block' }}
            />
          </div>

          <p style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.05em', color: 'oklch(45% 0.07 35)', margin: '32px 0 8px' }}>
            服裝款式
          </p>
          <p style={{ fontSize: 14.5, lineHeight: 1.9, margin: '0 0 16px', color: 'oklch(34% 0.02 40)' }}>
            以我們自己製作的泳衣為主，依照主題不同偶爾會有其他品牌的泳裝，但服裝剪裁幾乎都一樣，通常只是材質跟顏色不同而已。
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              [swimImg1, '泳裝款式 - 藍'],
              [swimImg2, '泳裝款式 - 黑'],
              [swimImg3, '泳裝款式 - 灰'],
              [swimImg4, '泳裝款式 - 紅'],
            ].map(([src, alt]) => (
              <img
                key={alt}
                src={src}
                alt={alt}
                onClick={openLightbox}
                style={{ width: '100%', aspectRatio: '3 / 4', objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in', display: 'block' }}
              />
            ))}
          </div>
        </Reveal>

        <Divider />

        {/* 其他說明 */}
        <Reveal style={{ padding: '48px 32px' }}>
          <p style={eyebrow}>07</p>
          <h2 style={heading}>其他說明</h2>
          <ul style={listStyle}>
            <li>為了拍攝效果可能會需要在皮膚上塗嬰兒油等可以協助反光的材料，會與您事先溝通之後再使用，若不方便使用也沒關係。</li>
            <li>拍攝期間若有任何不能接受的動作，您可以隨時提出。</li>
          </ul>
        </Reveal>

        {/* 作品集 */}
        <Reveal style={{ padding: '56px 32px 24px', background: 'oklch(94% 0.018 48)' }}>
          <p style={eyebrow}>08</p>
          <h2 style={{ ...heading, margin: '0 0 6px' }}>作品集</h2>
          <p style={{ fontSize: 13, color: 'oklch(48% 0.02 40)', margin: '0 0 24px' }}>Portfolio</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {galleryA.map((g) => (
              <img
                key={g.id}
                src={g.src}
                alt={g.label}
                onClick={openLightbox}
                style={{ width: '100%', aspectRatio: '3 / 4', objectFit: 'cover', borderRadius: 4, cursor: 'zoom-in', display: 'block' }}
              />
            ))}
          </div>
        </Reveal>

        {/* FOOTER */}
        <div style={{ padding: '48px 32px 60px', textAlign: 'center', background: 'oklch(94% 0.018 48)' }}>
          <div style={{ width: 36, height: 1, background: 'oklch(60% 0.04 40)', margin: '0 auto 22px' }} />
          <p style={{ fontFamily: "'Noto Serif TC',serif", fontSize: 15, margin: '0 0 8px', letterSpacing: '0.02em' }}>
            Mizugi Space
          </p>
          <p style={{ fontSize: 12.5, color: 'oklch(50% 0.02 40)', margin: '0 0 18px', lineHeight: 1.8 }}>
            因為熱愛所以開始的攝影企劃
          </p>
          <a href="/aose-hiyori" style={{ fontSize: 12, color: 'oklch(48% 0.08 35)', textDecoration: 'underline' }}>
            官方原創角色 青瀬ひより →
          </a>
        </div>
      </div>

      {lightbox.src && (
        <div
          onClick={closeLightbox}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'oklch(20% 0.01 40 / 0.9)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 20,
            padding: 28,
            cursor: 'zoom-out',
          }}
        >
          <img
            src={lightbox.src}
            alt={lightbox.label}
            onClick={stopClick}
            style={{
              maxWidth: '100%',
              maxHeight: '70vh',
              borderRadius: 6,
              boxShadow: '0 30px 80px oklch(0% 0 0 / 0.5)',
              cursor: 'default',
            }}
          />
          <button
            onClick={closeLightbox}
            style={{
              appearance: 'none',
              background: 'transparent',
              border: '1px solid oklch(95% 0.01 40 / 0.4)',
              color: 'oklch(95% 0.01 40)',
              fontFamily: "'Noto Sans TC',sans-serif",
              fontSize: 13,
              letterSpacing: '0.15em',
              padding: '10px 32px',
              borderRadius: 999,
              cursor: 'pointer',
            }}
          >
            關閉
          </button>
        </div>
      )}
    </div>
  );
}
